// src/components/Inspection/InspectionCard.jsx
import React from 'react';
import { ChevronRight, AlertCircle, CreditCard } from 'lucide-react';

const InspectionCard = ({ 
  inspection, 
  onViewDetails, 
  onReschedule, 
  onCancel
}) => {
  // ✅ Get inspection status color
  const getStatusColor = (status) => {
    const colors = {
      pending: '#FFA500',
      confirmed: '#4ECDC4',
      scheduled: '#45B7D1',
      in_progress: '#F39C12',
      completed: '#2ECC71',
      approved: '#27AE60',
      rejected: '#E74C3C',
      cancelled: '#999'
    };
    return colors[status] || '#666';
  };

  // ✅ Get payment status color
  const getPaymentStatusColor = (status) => {
    const colors = {
      initialized: '#FFA500',    // Orange - Processing
      pending: '#FF6B6B',        // Red - Waiting
      success: '#4ECDC4',        // Teal - Paid
      failed: '#E74C3C',         // Dark Red - Error
      refunded: '#9B59B6'        // Purple - Refunded
    };
    return colors[status] || '#999';
  };

  // ✅ Get payment status label
  const getPaymentStatusLabel = (status) => {
    const labels = {
      initialized: 'Processing Payment',
      pending: 'Payment Pending',
      success: 'Payment Completed',
      failed: 'Payment Failed',
      refunded: 'Refunded'
    };
    return labels[status] || 'Unknown';
  };

  // ✅ Get inspection status label
  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      scheduled: 'Scheduled',
      in_progress: 'In Progress',
      completed: 'Completed',
      approved: 'Approved',
      rejected: 'Rejected',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  // ✅ Can reschedule/cancel check
  const canReschedule = ['pending', 'confirmed'].includes(inspection?.status);
  const canCancel = ['pending', 'confirmed'].includes(inspection?.status);

  return (
    <div style={styles.card}>
      {/* Card Header */}
      <div style={styles.cardHeader}>
        <div>
          <h3 style={styles.carTitle}>
            {inspection?.car_brand} {inspection?.model}
          </h3>
          <p style={styles.carYear}>{inspection?.year}</p>
        </div>
        <span 
          style={{
            ...styles.statusBadge,
            backgroundColor: getStatusColor(inspection?.status)
          }}
        >
          {getStatusLabel(inspection?.status)}
        </span>
      </div>

      {/* Card Content */}
      <div style={styles.cardContent}>
        <div style={styles.contentGrid}>
          {/* Inspection Type & Location */}
          <div style={styles.contentItem}>
            <span style={styles.label}>Inspection Type:</span>
            <span style={styles.value}>
              {inspection?.inspection_type === 'home' ? 'Home Inspection' : 'Branch Inspection'}
            </span>
          </div>

          {/* Date & Time */}
          <div style={styles.contentItem}>
            <span style={styles.label}>Date & Time:</span>
            <span style={styles.value}>
              {inspection?.preferred_date ? (
                <>
                  {new Date(inspection.preferred_date).toLocaleDateString('en-IN')} at{' '}
                  {inspection?.preferred_time === 'undefined AM' ? 'TBD' : inspection?.preferred_time}
                </>
              ) : (
                'TBD'
              )}
            </span>
          </div>

          {/* Location */}
          <div style={styles.contentItem}>
            <span style={styles.label}>Location:</span>
            <span style={styles.value}>{inspection?.city}</span>
          </div>

          {/* Contact */}
          <div style={styles.contentItem}>
            <span style={styles.label}>Contact:</span>
            <span style={styles.value}>{inspection?.contact_person}</span>
          </div>

          {/* Phone */}
          <div style={styles.contentItem}>
            <span style={styles.label}>Phone:</span>
            <span style={styles.value}>{inspection?.contact_phone}</span>
          </div>

          {/* ✅ INSPECTION FEE */}
          <div style={styles.contentItem}>
            <span style={styles.label}>Inspection Fee:</span>
            <span style={styles.valuePriceYellow}>
              ₹{inspection?.inspection_fee || 0}
            </span>
          </div>
        </div>

        {/* ✅ PAYMENT STATUS SECTION */}
        {inspection?.payment_status && (
          <div style={styles.paymentSection}>
            <div style={styles.paymentHeader}>
              <CreditCard size={18} color="#003566" />
              <span style={styles.paymentTitle}>Payment Status</span>
            </div>
            <div style={styles.paymentContent}>
              <span 
                style={{
                  ...styles.paymentStatusBadge,
                  backgroundColor: getPaymentStatusColor(inspection.payment_status)
                }}
              >
                {getPaymentStatusLabel(inspection.payment_status)}
              </span>
              {inspection?.payment_id && (
                <span style={styles.paymentId}>
                  ID: {inspection.payment_id}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Card Footer - Action Buttons */}
      <div style={styles.cardFooter}>
        <button
          onClick={() => onViewDetails(inspection)}
          style={styles.buttonPrimary}
        >
          View Details <ChevronRight size={18} />
        </button>

        {canReschedule && (
          <button
            onClick={() => onReschedule(inspection)}
            style={styles.buttonSecondary}
          >
            Reschedule
          </button>
        )}

        {canCancel && (
          <button
            onClick={() => onCancel(inspection?.id)}
            style={styles.buttonDanger}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

const styles = {
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    border: '1px solid #e0e0e0'
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px 20px',
    backgroundColor: '#f9f9f9',
    borderBottom: '1px solid #e0e0e0'
  },

  carTitle: {
    fontSize: '1.3rem',
    fontWeight: '700',
    color: '#003566',
    margin: '0 0 4px 0'
  },

  carYear: {
    fontSize: '0.9rem',
    color: '#666',
    margin: '0'
  },

  statusBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
    whiteSpace: 'nowrap'
  },

  cardContent: {
    padding: '20px'
  },

  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '0'
  },

  contentItem: {
    display: 'flex',
    flexDirection: 'column'
  },

  label: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#999',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },

  value: {
    fontSize: '14px',
    color: '#333',
    fontWeight: '500'
  },

  valuePriceYellow: {
    fontSize: '16px',
    color: '#FFD60A',
    fontWeight: '700'
  },

  // ✅ PAYMENT SECTION STYLES
  paymentSection: {
    marginTop: '16px',
    padding: '14px',
    backgroundColor: '#f0f8ff',
    border: '1px solid #e0e8f0',
    borderRadius: '8px'
  },

  paymentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px'
  },

  paymentTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#003566'
  },

  paymentContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },

  paymentStatusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
    width: 'fit-content'
  },

  paymentId: {
    fontSize: '12px',
    color: '#666',
    fontFamily: 'monospace'
  },

  cardFooter: {
    display: 'flex',
    gap: '10px',
    padding: '16px 20px',
    backgroundColor: '#f9f9f9',
    borderTop: '1px solid #e0e0e0',
    flexWrap: 'wrap'
  },

  buttonPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    backgroundColor: '#003566',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },

  buttonSecondary: {
    padding: '10px 16px',
    backgroundColor: '#FFD60A',
    color: '#003566',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },

  buttonDanger: {
    padding: '10px 16px',
    backgroundColor: '#E74C3C',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }
};

export default InspectionCard;